import { Typography } from '@mui/material';
import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { getAmount } from 'src/components/SidePanelTabs/MembershipInformation/subscription-details.utils';
import { SubscriptionsReportRow } from 'src/graphql/entities/subscriptions-report/subscriptions-report.types';
import { STRIPE_STATUS_PRETTY } from './const';
import { getFullAddress } from './utils';

export const useSubscriptionsReportTableColumns = () => {
  return useMemo(
    (): MRT_ColumnDef<SubscriptionsReportRow>[] => [
      {
        accessorKey: 'rowNumber',
        header: '#',
        size: 50,
        Cell: ({ row }) => row.index + 1,
        enableSorting: false,
      },
      {
        accessorKey: 'customerName',
        header: 'Client Name',
        Cell: ({ row }) => <Typography>{row.original.customerName}</Typography>,
        enableSorting: true,
        enableResizing: true,
      },
      {
        accessorKey: 'address1',
        header: 'Address',
        sortingFn: (a, b) => {
          const fullAddressA = getFullAddress(a.original).toLowerCase();
          const fullAddressB = getFullAddress(b.original).toLowerCase();
          return fullAddressA.localeCompare(fullAddressB);
        },
        GroupedCell: ({ row }) => <Typography>{getFullAddress(row.original)}</Typography>,
        enableSorting: true,
        size: 300,
      },
      {
        accessorKey: 'productName',
        header: 'Product',
        Cell: ({ row }) => <Typography>{row.original.productName}</Typography>,
        enableSorting: true,
      },
      {
        accessorKey: 'segment',
        header: 'Segment',
        Cell: ({ row }) => <Typography>{row.original.segment}</Typography>,
        enableSorting: true,
      },
      {
        accessorKey: 'latestInvoiceSubtotal',
        header: 'Latest Invoice Subtotal',
        Cell: ({ row }) => (
          <Typography>{getAmount(row.original.latestInvoiceSubtotal || 0)}</Typography>
        ),
        enableSorting: true,
        size: 220,
      },
      {
        accessorKey: 'interval',
        header: 'Interval',
        sortingFn: (a, b) => {
          const monthsA =
            a.original.interval === 'year'
              ? (a.original.intervalCount || 0) * 12
              : a.original.intervalCount || 0;
          const monthsB =
            b.original.interval === 'year'
              ? (b.original.intervalCount || 0) * 12
              : b.original.intervalCount || 0;
          return monthsA - monthsB;
        },
        Cell: ({ row }) => (
          <Typography>
            {row.original.intervalCount}-{row.original.interval}
          </Typography>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'createdAt',
        header: 'Created At',
        Cell: ({ row }) => <Typography>{row.original.createdAt}</Typography>,
        enableSorting: true,
      },
      {
        accessorKey: 'currentPeriodEnd',
        header: 'Renews At',
        Cell: ({ row }) => <Typography>{row.original.currentPeriodEnd}</Typography>,
        enableSorting: true,
      },
      {
        accessorKey: 'latestInvoiceDiscount',
        header: 'Latest Invoice Discount',
        Cell: ({ row }) => (
          <Typography>{getAmount(row.original.latestInvoiceDiscount || 0)}</Typography>
        ),
        enableSorting: true,
        size: 220,
      },
      {
        accessorKey: 'businessName',
        header: 'Business Name',
        Cell: ({ row }) => <Typography>{row.original.businessName}</Typography>,
        enableSorting: true,
        size: 300,
      },
      {
        accessorKey: 'city',
        header: 'City',
        Cell: ({ row }) => <Typography>{row.original.city}</Typography>,
        enableSorting: true,
      },
      {
        accessorKey: 'customerEmail',
        header: 'Client Email',
        Cell: ({ row }) => <Typography>{row.original.customerEmail}</Typography>,
        enableSorting: true,
        enableClickToCopy: true,
      },
      {
        accessorKey: 'subscriptionId',
        header: 'Subscription ID',
        Cell: ({ row }) => <Typography>{row.original.subscriptionId}</Typography>,
        enableSorting: true,
        enableClickToCopy: true,
        size: 300,
      },
      {
        accessorKey: 'invoiceSubtotal',
        header: 'Invoice Subtotal',
        Cell: ({ row }) => (
          <Typography>{getAmount(row.original.invoiceSubtotal || 0)}</Typography>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'chargeAmount',
        header: 'Charge Amount',
        Cell: ({ row }) => (
          <Typography>{getAmount(row.original.chargeAmount || 0)}</Typography>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        Cell: ({ row }) => (
          <Typography>{STRIPE_STATUS_PRETTY[row.original.status]}</Typography>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'canceledAt',
        header: 'Ended At',
        Cell: ({ row }) => <Typography>{row.original.canceledAt}</Typography>,
        enableSorting: true,
      },
    ],
    [],
  );
};
